import { useState } from 'react';
import {
  AlertBanner,
  Badge,
  Button,
  Dialog,
  EmptyState,
  Input,
  ProcessingBasisNote,
  StatCard,
  Toast,
} from '@assist/ui';
import { renderProblem } from '@assist/i18n';
import { ApiError, endpoints } from '../state/api.js';
import { useSession } from '../state/session.js';
import { useResource } from '../lib/useResource.js';
import { Screen } from '../components/Screen.js';
import { formatDate } from '../lib/format.js';
import { usePeriode } from '../state/periode.js';
import { awalBulan } from '../lib/periode.js';

/**
 * ESKALASI & SOROTAN — Figma `116:1192` · M07 · rute `/meetings/escalation`.
 *
 * Izin: MGR penuh; LDR hanya lihat (peta §5.3 — `MGR, LDR_READ_ONLY` di manifest).
 *
 * "TANPA ESKALASI" ADALAH KONDISI BAIK, DAN ITU DUA KALIMAT YANG BERBEDA
 * --------------------------------------------------------------------
 * Peta menuliskannya dengan tegas: *"tanpa eskalasi (kondisi baik, bukan kosong netral)"*.
 * Sebuah layar yang merender `EmptyState` bawaan atas daftar kosong akan berbunyi "Belum
 * ada data" — yang menyampaikan kebalikan dari yang sesungguhnya terjadi. Server
 * mengirimkan `nothing_escalated` sebagai medan TERSENDIRI supaya layar tidak perlu
 * menyimpulkan makna dari `items.length === 0`, dan supaya dua layar tidak dapat
 * menyimpulkannya berbeda.
 *
 * TANGGA EMPAT TINGKATNYA ADALAH DATA
 * ----------------------------------
 * 24 / 48 / 72 jam datang dari `escalation_rule`, bukan dari kalimat di layar. Sebuah
 * organisasi yang mengubahnya akan melihat angka yang benar tanpa penggelaran — dan sebuah
 * layar yang menuliskannya akan berbohong pada organisasi pertama yang mengubahnya.
 *
 * MENGUBAHNYA lewat dialog konfirmasi, dan tercatat di jejak audit dengan nilai SEBELUM
 * dan SESUDAH. "Aturannya diubah" tanpa menyebut dari apa ke apa tidak dapat
 * ditindaklanjuti setahun kemudian.
 */
export function EscalationSpotlight({ onOpenFinding }: { onOpenFinding: (code: string) => void }) {
  const { t, locale, translator, me } = useSession();
  const readOnly = me?.role_code !== 'MGR';
  /** Periode dari bilah atas (`5:58`). */
  const { periode } = usePeriode();

  const data = useResource(
    async () => {
      const [escalations, rules, spotlight] = await Promise.all([
        endpoints.escalations(),
        endpoints.escalationRules(),
        endpoints.spotlight(awalBulan(periode)),
      ]);
      return { escalations, rules, spotlight };
    },
    [periode],
  );

  const [editingRules, setEditingRules] = useState(false);
  const [sla, setSla] = useState<Record<number, string>>({});
  const [removing, setRemoving] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  async function simpanAturan() {
    if (!data.data) return;
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.putEscalationRules({
        levels: data.data.rules.levels.map((l) => ({
          level: l.level,
          target_role_code: l.target_role_code,
          sla_hours: Number(sla[l.level] ?? l.sla_hours),
        })),
      });
      setToast(t('escalation.rules.saved'));
      setEditingRules(false);
      await data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  async function cabutSorotan(id: string) {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.removeSpotlight(id);
      setToast(t('spotlight.removed'));
      setRemoving(null);
      await data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen
      title={t('escalation.title')}
      subtitle={t('escalation.subtitle')}
      resource={data}
      moduleCode="M07"
      actions={
        readOnly ? undefined : (
          <Button
            variant="secondary"
            onClick={() => {
              setSla(
                Object.fromEntries(
                  (data.data?.rules.levels ?? []).map((l) => [l.level, String(l.sla_hours)]),
                ),
              );
              setEditingRules(true);
            }}
          >
            {t('escalation.rules.edit')}
          </Button>
        )
      }
    >
      {(loaded) => (
        <>
          {problem ? (
            <AlertBanner
              kind="danger"
              signalWord={t('signal.danger')}
              title={renderProblem(translator, problem.problem)}
            />
          ) : null}

          {readOnly ? (
            <AlertBanner
              kind="information"
              signalWord={t('signal.information')}
              title={t('escalation.readOnly')}
            />
          ) : null}

          <div className="stat-grid">
            {loaded.escalations.by_level.map((l) => (
              <StatCard
                key={l.level}
                label={t('escalation.level', { level: l.level })}
                value={String(l.open_count)}
                caption={t('escalation.level.caption', {
                  /*
                    `escalation.target.*`, BUKAN `role.*`. Tingkat 4 menunjuk daftar penerima
                    (`target_kind = DISTRIBUTION`), dan `role.DISTRIBUTION` tidak ada di
                    katalog — sehingga kartu tingkat 4 menampilkan kunci i18n MENTAH
                    (`role.DISTRIBUTION`) kepada manajer. Gerbang teks antarmuka hijau atas
                    ini karena ia memeriksa kunci yang ditulis STATIS, dan kunci ini dirakit.
                    Ditemukan dengan melihat tangkapan layarnya, bukan dengan menjalankan
                    gerbangnya — persis cara yang diminta.
                  */
                  role: t(
                    `escalation.target.${loaded.rules.levels.find((r) => r.level === l.level)?.target_role_code ?? 'MGR'}`,
                  ),
                  hours: loaded.rules.levels.find((r) => r.level === l.level)?.sla_hours ?? 0,
                })}
              />
            ))}
          </div>

          <section className="card">
            <h2 className="card__title">{t('escalation.open')}</h2>
            {/*
              KONDISI BAIK, bukan kosong netral. Dua kalimat yang berbeda, dan yang
              membedakannya adalah medan dari server — bukan `items.length`.
            */}
            {loaded.escalations.nothing_escalated ? (
              <EmptyState
                title={t('escalation.none.title')}
                body={t('escalation.none.body')}
                icon={<span aria-hidden="true">✓</span>}
              />
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t('escalation.column.level')}</th>
                    <th scope="col">{t('findings.column.code')}</th>
                    <th scope="col">{t('findings.column.title')}</th>
                    <th scope="col">{t('escalation.column.trigger')}</th>
                    <th scope="col">{t('escalation.column.target')}</th>
                    <th scope="col">{t('escalation.column.due')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loaded.escalations.items.map((row) => (
                    <tr key={row.id} className={row.overdue_at_level ? 'is-overdue' : ''}>
                      <td>
                        <Badge tone={row.level >= 3 ? 'danger' : 'warning'}>
                          {t('escalation.level', { level: row.level })}
                        </Badge>
                      </td>
                      <td>
                        <button
                          className="linkish"
                          type="button"
                          onClick={() => row.item.code && onOpenFinding(row.item.code)}
                        >
                          {row.item.code ?? '—'}
                        </button>
                      </td>
                      <td>
                        {row.item.title}
                        {row.item.is_safety ? (
                          <>
                            {' '}
                            <Badge tone="danger">{t('category.SAFETY')}</Badge>
                          </>
                        ) : null}
                      </td>
                      <td>{t(`escalation.trigger.${row.trigger_code}`)}</td>
                      <td>{t(`role.${row.target_role_code}`)}</td>
                      <td>{formatDate(row.due_at, locale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="card">
            <h2 className="card__title">{t('escalation.ladder')}</h2>
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('escalation.column.level')}</th>
                  <th scope="col">{t('escalation.column.target')}</th>
                  <th scope="col">{t('escalation.column.sla')}</th>
                  <th scope="col">{t('escalation.column.triggers')}</th>
                </tr>
              </thead>
              <tbody>
                {loaded.rules.levels.map((l) => (
                  <tr key={l.level}>
                    <td>{t('escalation.level', { level: l.level })}</td>
                    <td>
                      {/*
                        Tingkat 4 menunjuk DAFTAR PENERIMA, bukan peran (keputusan sementara
                        K-03). Server mengirim `target_kind` sebagai nilainya, dan kalimatnya
                        datang dari katalog — bukan dikarang sebagai "MGR", yang akan
                        menampilkan peran yang tidak memegang hak apa pun.
                      */}
                      {t(`escalation.target.${l.target_role_code}`)}
                    </td>
                    <td>{t('escalation.sla.value', { hours: l.sla_hours })}</td>
                    <td>{l.triggers.map((tr) => t(`escalation.trigger.${tr}`)).join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {loaded.rules.processing_basis ? (
              <ProcessingBasisNote
                label={t('privacy.basisNote.label')}
                sentence={t('privacy.basisNote.sentence', {
                  basis: t(`privacy.basis.${loaded.rules.processing_basis.legal_basis}`),
                })}
                article={loaded.rules.processing_basis.article}
                purposes={t('privacy.basisNote.purposes', {
                  purposes: loaded.rules.processing_basis.purposes
                    .map((p) => t(`privacy.purpose.${p}`))
                    .join(', '),
                })}
                moreLabel={t('privacy.basisNote.more')}
              />
            ) : null}
          </section>

          <section className="card">
            <h2 className="card__title">{t('spotlight.title')}</h2>
            {loaded.spotlight.items.length === 0 ? (
              <p className="card__muted">{t('spotlight.empty')}</p>
            ) : (
              <ul className="spotlight">
                {loaded.spotlight.items.map((s) => (
                  <li key={s.id}>
                    <p className="spotlight__headline">
                      {/*
                        Kalimat sorotan ditulis MANUSIA dan disimpan dalam dua kolom
                        (`headline_id`, `headline_en`). Ia bukan kunci katalog — jadi yang
                        dipilih di sini adalah kolom yang sesuai bahasa pembaca, dan
                        keduanya wajib ada di basis data.
                      */}
                      {locale === 'id-ID' ? s.headline_id : s.headline_en}
                    </p>
                    {s.note ? <p className="card__muted">{s.note}</p> : null}
                    <p className="card__muted">
                      {t(`spotlight.subject.${s.subject_kind}`)}
                      {s.subject_label ? `: ${s.subject_label}` : ''}
                    </p>
                    {readOnly ? null : (
                      <Button variant="text" onClick={() => setRemoving(s.id)}>
                        {t('spotlight.remove')}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <Dialog
            open={editingRules}
            kind="confirm"
            title={t('escalation.rules.edit')}
            description={t('escalation.rules.describe')}
            confirmLabel={t('app.save')}
            cancelLabel={t('app.cancel')}
            busy={busy}
            onConfirm={() => void simpanAturan()}
            onCancel={() => setEditingRules(false)}
          >
            {(loaded.rules.levels ?? []).map((l) => (
              <Input
                key={l.level}
                label={t('escalation.rules.slaFor', {
                  level: l.level,
                  role: t(`escalation.target.${l.target_role_code}`),
                })}
                inputType="number"
                min={1}
                max={8760}
                value={sla[l.level] ?? String(l.sla_hours)}
                onChange={(e) => setSla({ ...sla, [l.level]: e.target.value })}
              />
            ))}
          </Dialog>

          <Dialog
            open={removing !== null}
            kind="confirm"
            title={t('spotlight.remove')}
            description={t('spotlight.remove.describe')}
            confirmLabel={t('spotlight.remove')}
            cancelLabel={t('app.cancel')}
            busy={busy}
            onConfirm={() => {
              if (removing) void cabutSorotan(removing);
            }}
            onCancel={() => setRemoving(null)}
          />

          {toast ? (
            <div className="assist-toast-stack">
              <Toast
                kind="success"
                signalWord={t('signal.success')}
                title={toast}
                onDismiss={() => setToast(null)}
                closeLabel={t('app.dismiss')}
              />
            </div>
          ) : null}
        </>
      )}
    </Screen>
  );
}
